import { App } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useMatch, useNavigate } from 'react-router-dom';
import { AntivirusScanStatusEnum } from '../../enums/AntivirusScanStatusEnum';
import { FilesTableEnum } from '../../enums/FilesTableEnum';
import { SettingTypeEnum } from '../../enums/SettingTypeEnum';
import { SpecialFolderTypeEnum } from '../../enums/SpecialFolderTypeEnum';
import type File from '../../interfaces/File';
import type FileAccess from '../../interfaces/FileAccess';
import type { GetFileAccessesPayload } from '../../interfaces/FileApiService';
import type FileDetail from '../../interfaces/FileDetail';
import type { FilePublicShareLinkItem } from '../../interfaces/FilePublicShareLinkItem';
import type FileVersion from '../../interfaces/FileVersion';
import { FileService } from '../../services/fileApiService';
import { useAppStore } from '../../store/store';
import { isAntivirusBlocked } from '../../utils/antivirus';
import { hasSettingLimitation } from '../../utils/utils';
import { useAuth } from '../useAuth';
import { useFolderContentStats } from '../useFolderContentStats';
import useFilesActions from '../useFilesActions';
import useOpenFile from '../useOpenFile';
import { useTags } from '../useTags';

// Maximum number of characters allowed in the file description.
export const DESCRIPTION_CHARACTERS_LIMIT = 10000;

const fileService = new FileService();

export interface UseFileDetailTabParams {
  file: File;
}

/**
 * Custom hook to handle the logic for the file detail tab.
 * @param file - The file object to show details for.
 */
export function useFileDetailTab({ file }: UseFileDetailTabParams) {
  // Translation hook
  const { t } = useTranslation();
  // App context from AntD, providing modal, notification, message, etc.
  const { modal, notification, message } = App.useApp();
  // Authenticated user and file route helpers
  const { authenticatedUser, isInFileRoute } = useAuth();
  // File action handlers with the parent folder ID as context
  const { handleShareFiles, handleOpenFileFromTrash, handleCreatePublicLink } = useFilesActions({
    folderUid: file.parentId?.toString(),
  });
  // Helper to open a file safely (e.g., with permission or error handling)
  const { openFileSafely } = useOpenFile();

  // UI and state management
  const [isLoading, setIsLoading] = useState(true);
  const [showMoreVersions, setShowMoreVersions] = useState(false);
  const [isShowMoreVersionsButtonVisible, setIsShowMoreVersionsButtonVisible] = useState(false);
  const [fileVersions, setFileVersions] = useState<FileVersion[]>([]);
  const [isLoadingFileVersions, setIsLoadingFileVersions] = useState(false);
  const [fileTags, setFileTags] = useState<FileDetail['tags']>([]);
  const [fileAccesses, setFileAccesses] = useState<FileAccess[]>([]);
  const [totalFileAccesses, setTotalFileAccesses] = useState(0);
  const [fileDescription, setFileDescription] = useState('');
  const [fileCreatedAt, setFileCreatedAt] = useState<FileDetail['createdAt'] | null>(
    file.createdAt ?? null,
  );
  // Store info about file selling (e.g., price, revenue, etc.)
  const [fileSellInfo, setFileSellInfo] = useState<null | {
    price: number;
    revenue: number;
    unitsSold: number;
    currency?: string;
  }>(null);

  const { canRefreshFolderStats, folderStats, isLoadingFolderStats, loadFolderStats } =
    useFolderContentStats(file);

  // Ref for debouncing textarea input
  const debounceTextareaTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Ref for scrolling the file versions list
  const versionsScrollRef = useRef<HTMLDivElement>(null);

  // Get the current section of the files route (e.g., 'personal', 'shared')
  const matchFilesSection = useMatch('/files/:section/*');
  const navigate = useNavigate();
  const currentSection = matchFilesSection?.params?.section || 'personal';

  // Get tags from global store
  const { tags } = useTags();
  // Get denied settings from global store
  const deniedSettings = useAppStore((s) => s.deniedSettings);

  // Whether public link sharing is enabled (not denied by settings)
  const isPublicLinkSettingEnabled = useMemo(
    () => !hasSettingLimitation(deniedSettings ?? null, SettingTypeEnum.PublicAccessLink),
    [deniedSettings],
  );

  // State for AI details display and public share link
  const [showAIDetails, setShowAIDetails] = useState(false);
  const [publicShareLink, setPublicShareLink] = useState<FilePublicShareLinkItem | null>(null);

  /**
   * Checks if a file is owned by the authenticated user.
   * @param f - The file to check.
   * @returns True if the file is owned by the user.
   */
  const isFileMine = useCallback(
    (f: File) => {
      return f.ownerId === authenticatedUser?.id;
    },
    [authenticatedUser?.id],
  );

  /**
   * Mapping of classification status numeric codes to strings for display.
   */
  const classificationStatusMap = useMemo(
    () => ({
      0: t('aiClassificationStatus.none'),
      1: t('aiClassificationStatus.classifying'),
      2: t('aiClassificationStatus.done'),
      3: t('aiClassificationStatus.error'),
      4: t('aiClassificationStatus.queued'),
    }),
    [t],
  );

  /**
   * Handle scrolling of the versions list, determining when to show or hide the "Show more versions" button.
   */
  const handleVersionsScroll = () => {
    const el = versionsScrollRef.current;
    if (!el) return;

    const { scrollTop, clientHeight, scrollHeight } = el;

    // If the scroll is near the top and not at the bottom, show the button.
    const nearTop = scrollTop <= 10;
    const atBottom = scrollTop + clientHeight >= scrollHeight - 10;

    setIsShowMoreVersionsButtonVisible(nearTop && !atBottom);
  };

  /**
   * Fetch file accesses for the file (who has accessed/shared, etc.)
   */
  const getFileAccesses = useCallback(async () => {
    const payload: GetFileAccessesPayload = {
      files: [file?.id?.toString()],
      offset: 0,
      count: 4,
    };

    fileService
      .getFileAccesses(payload)
      .then((response) => {
        setFileAccesses(response.accesses);
        setTotalFileAccesses(response.total);
      })
      .catch(() => {
        notification.error({
          title: t('errors.hitASnag'),
        });
      });
  }, [file, notification, t]);

  /**
   * Fetch all available versions of the file.
   */
  const getFileVersions = useCallback(async () => {
    setIsLoadingFileVersions(true);
    fileService
      .getFileVersions(file.id)
      .then((response) => {
        setFileVersions(response);
      })
      .catch(() => {
        notification.error({
          title: t('errors.hitASnag'),
        });
      })
      .finally(() => {
        setIsLoadingFileVersions(false);
      });
  }, [file, notification, t]);

  /**
   * Fetch main file details and update various states (description, tags, sell info).
   */
  const getFile = useCallback(async () => {
    if (!file) {
      setIsLoading(false);
      return;
    }

    fileService
      .getFile(file.id)
      .then((response) => {
        setFileCreatedAt(response.createdAt ?? file.createdAt ?? null);
        setFileDescription(response?.description);
        setFileSellInfo(response);
        setFileTags(response.tags);
      })
      .catch(() => {
        notification.error({
          title: t('errors.genericFetchError', {
            resource: t('common.file'),
          }),
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [file, notification, t]);

  /**
   * Emits a file updated event for listeners elsewhere in the app.
   * @param updatedFiles - Array of files that were updated.
   */
  const onFileUpdated = (updatedFiles: File[]) => {
    useAppStore.getState().events.emit.onFilesUpdated({ files: updatedFiles });
  };

  /**
   * Handler for changes to the file description textarea.
   * Debounces API save, provides success/failure notification.
   */
  const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (!isFileMine(file)) {
      return;
    }

    const { value } = e.target;

    if (value.length <= DESCRIPTION_CHARACTERS_LIMIT) {
      setFileDescription(value);

      // Debounce the API request to avoid spamming on fast input.
      if (debounceTextareaTimeoutRef.current) {
        clearTimeout(debounceTextareaTimeoutRef.current);
      }

      debounceTextareaTimeoutRef.current = setTimeout(() => {
        fileService
          .updateFileDescription(file.id, value)
          .then(() => {
            message.success(
              t('notifications.genericUpdateSuccess', {
                resource: t('common.description'),
              }),
            );
          })
          .catch(() => {
            notification.error({
              title: t('errors.genericUpdateError', {
                resource: t('common.description'),
              }),
            });
          });
      }, 500);
    }
  };

  /**
   * Opens the given file or folder, handling trash case, navigation, and regular files.
   * @param f - File or folder to open.
   */
  const handleOpenFile = (f: File) => {
    if (f.isFolder && isInFileRoute(FilesTableEnum.Trash)) {
      // If it is a folder in the Trash, use special handler.
      handleOpenFileFromTrash(f);
    } else if (f.isFolder && !isInFileRoute(FilesTableEnum.Trash)) {
      // Regular folder: navigate into the folder.
      navigate(`/files/${currentSection}/folder/${f.id}`, {
        replace: true,
      });
    } else {
      // If it's a file, open it
      openFileSafely(f);
    }
  };

  /**
   * Handler to restore a specific version of a file with confirmation prompt.
   */
  const handleRestoreFileVersion = (fileId: number, versionId: number, version: number) => {
    modal.confirm({
      title: t('modal.restoreFileVersion.title'),
      icon: null,
      centered: true,
      content: (
        <Trans>
          <p>
            {t('modal.restoreFileVersion.description', {
              resource: file?.fileName,
              version: version,
            })}
          </p>
        </Trans>
      ),
      onOk: () => {
        return fileService
          .restoreFileVersion(fileId, versionId)
          .then(() => {
            message.success(
              t('notifications.fileVersionRestored', {
                resource: file?.fileName,
              }),
            );
            onFileUpdated([
              {
                ...file,
                // Infected is sticky: restore does not clear quarantine. Clean files go Pending.
                ...(isAntivirusBlocked(file)
                  ? {}
                  : {
                      avScanStatus: AntivirusScanStatusEnum.Pending,
                      avThreatName: '',
                      avScanError: '',
                    }),
              },
            ]);
            // Refresh file versions after restoring.
            return getFileVersions();
          })
          .catch((error) => {
            notification.error({
              title: t('errors.hitASnag'),
            });
            throw error;
          });
      },
      onCancel: () => {},
    });
  };

  /**
   * Checks whether the "Manage Access" UI should be shown.
   * Visible if not in trash, owned by user, and not a QuickShare folder.
   */
  const isManageAccessVisible = useMemo(() => {
    return (
      !isInFileRoute(FilesTableEnum.Trash) &&
      file?.ownerId === authenticatedUser?.id &&
      file?.specialFolder !== SpecialFolderTypeEnum.QuickShare
    );
  }, [file, authenticatedUser, isInFileRoute]);

  /**
   * Returns the file versions sorted in descending order (latest first).
   */
  const sortedFileVersions = useMemo(() => {
    return [...fileVersions].sort((a, b) => b.id - a.id);
  }, [fileVersions]);

  /**
   * Effect to load file details, accesses, versions, and public share link (if owned by user).
   * Runs when the file object identity changes.
   */
  useEffect(() => {
    setFileAccesses([]);
    setFileCreatedAt(file.createdAt ?? null);
    if (file && !isInFileRoute(FilesTableEnum.Trash)) {
      const promises = [getFile()];

      if (isFileMine(file)) {
        promises.push(getFileAccesses());
        promises.push(getFileVersions());
        void fileService.getPublicFileShareLink(file.id).then(setPublicShareLink);
      } else {
        setPublicShareLink(null);
      }

      void Promise.all(promises);
    } else {
      void getFile();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: refetch only when `file` identity changes
  }, [file]);

  /**
   * Reset UI for version list display when changing target file.
   */
  useEffect(() => {
    setShowMoreVersions(false);
    setIsShowMoreVersionsButtonVisible(true);
    if (versionsScrollRef.current) {
      versionsScrollRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [file.id]);

  /**
   * Gets a string suitable for a tooltip explaining all the tags associated with the file.
   * @returns Explanations in Markdown style, one line per tag.
   */
  const getTagExplanations = () => {
    if (!fileTags) return '';
    if (fileTags.length === 0) return '';
    return fileTags
      .map((tagIg) => {
        const tObj = tags.find((tag) => tag.id === tagIg.id);
        return `- ${tObj?.title}: ${tagIg.explanation}`;
      })
      .join('\n');
  };

  // Return all values and handlers needed by UI components related to the file detail tab.
  return {
    t,
    tags,
    isLoading,
    showMoreVersions,
    setShowMoreVersions,
    isShowMoreVersionsButtonVisible,
    sortedFileVersions,
    isLoadingFileVersions,
    fileTags,
    fileAccesses,
    totalFileAccesses,
    fileDescription,
    fileCreatedAt,
    fileSellInfo,
    versionsScrollRef,
    handleVersionsScroll,
    handleShareFiles,
    handleCreatePublicLink,
    onFileUpdated,
    isPublicLinkSettingEnabled,
    publicShareLink,
    classificationStatusMap,
    showAIDetails,
    setShowAIDetails,
    getTagExplanations,
    isManageAccessVisible,
    handleDescriptionChange,
    handleRestoreFileVersion,
    handleOpenFile,
    isFileMine,
    folderStats,
    isLoadingFolderStats,
    canRefreshFolderStats,
    loadFolderStats,
  };
}
